import { DefaultButton, Dropdown, MessageBar, MessageBarType, Pivot, PivotItem, Stack, TextField } from '@fluentui/react';
import React from 'react';
import ToggleField from '../../../../components/Common/ToggleField';
import { useGetEmailTemplateDefaultsQuery, useGetGlobalStorefrontSettingsQuery, useGetStorefrontQuery, usePatchStorefrontMutation } from '../../../../store/endpoints/StorefrontSettingsEndpoints';
import { useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { IStorefrontSettings, buildStorefrontPatch } from '../../../../models/Storefront';
import { RootState } from '../../../../store/rootReducer';
import StorefrontSettingsTitleBar from '../../../../components/Storefront/StorefrontSettingsTitleBar';
import useStorefrontEditLease from '../../../../hooks/useStorefrontEditLease';
import { StorefrontReadOnlyRegion } from '../../../../components/Storefront/StorefrontEditLock';
import { storefrontSaveErrorMessage } from '../../../../logic/storefrontLeaseConflict';
import RichTextField from '../../../../components/Common/RichTextField';
import { toast } from 'react-toastify';
import TokenReferenceButton from '../../../../components/Common/TokenReferenceButton';
import { useTokenSuggestions } from '../../../../components/Common/useTokenSuggestions';
import { useUrlTabState } from '../../../../hooks/useUrlState';
import ConditionalSetting from '../../../../components/Common/ConditionalSetting';

interface IStorefrontAuthenticationSettingsPageParams {
    storefront: string;
}

const DEBOUNCE_MS = 3000;

/**
 * The approval email's token reference, mounted inside the rich-text editor's overlay. That overlay
 * masks the whole page, so this is the only place it is reachable while the body is being written --
 * which is the only moment it is wanted.
 */
const approvedTokenReference = (
    <TokenReferenceButton
        context='signupApproved'
        description='Copy a token into the email body; it is replaced with the corresponding value when the approval email is sent. In the Show Source view the body also suggests them as you type an opening brace.'
    />
);

const StorefrontAuthenticationSettingsPage: React.FC<RouteComponentProps<IStorefrontAuthenticationSettingsPageParams>> = (props) => {
    // In the URL so a documentation link can land on the tab holding the setting it describes.
    const [selectedTab, setSelectedTab] = useUrlTabState('login');
    const { storefront } = props.match.params;
    const storefrontQuery = useGetStorefrontQuery(storefront, { skip: !storefront });
    const [patchStorefront] = usePatchStorefrontMutation();

    const [localSettings, setLocalSettingsRaw] = React.useState<IStorefrontSettings | undefined>(undefined);
    const localSettingsRef = React.useRef<IStorefrontSettings | undefined>();
    const originalSettingsRef = React.useRef<IStorefrontSettings | undefined>();
    const debounceSaveRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const globalSettings = useGetGlobalStorefrontSettingsQuery();
    // The default body the API would send when this field is blank. Shown in the editor as a starting
    // point, but deliberately NOT written into the settings: blank has to keep meaning "use whatever
    // the API sends", or a merchant who never edits it is frozen on today's wording forever.
    const approvedDefault = useGetEmailTemplateDefaultsQuery('signupApproved');
    // Offered as you type an opening brace in the body's Show Source view.
    const approvedTokens = useTokenSuggestions('signupApproved');
    const organization = useSelector((state: RootState) => state.settings.selectedOrganization);
    // Autosaving toggles, so a lease held by an assistant turn would turn each flip into a rejected
    // patch. One flag switches the form off instead.
    const { locked: readOnly } = useStorefrontEditLease(storefront);

    const setLocalSettings = React.useCallback((s: IStorefrontSettings) => {
        localSettingsRef.current = s;
        setLocalSettingsRaw(s);
    }, []);

    React.useEffect(() => {
        if (storefrontQuery.data && !originalSettingsRef.current) {
            setLocalSettings(storefrontQuery.data);
            originalSettingsRef.current = storefrontQuery.data;
        }
    }, [storefrontQuery.data, setLocalSettings]);

    const handleSettingsChanged = React.useCallback(() => {
        originalSettingsRef.current = undefined;
    }, []);

    React.useEffect(() => {
        return () => {
            if (debounceSaveRef.current) {
                clearTimeout(debounceSaveRef.current);
                if (localSettingsRef.current && originalSettingsRef.current) {
                    const patch = buildStorefrontPatch(originalSettingsRef.current, localSettingsRef.current);
                    if (Object.keys(patch).length > 0) {
                        patchStorefront({ id: storefront, patch });
                    }
                }
            }
        };
    }, []);

    const doSave = React.useCallback(async (current: IStorefrontSettings) => {
        if (debounceSaveRef.current) {
            clearTimeout(debounceSaveRef.current);
        }
        debounceSaveRef.current = setTimeout(() => {
            if (originalSettingsRef.current) {
                const patch = buildStorefrontPatch(originalSettingsRef.current, current);
                if (Object.keys(patch).length > 0) {
                    // The debounce can land after an assistant turn has taken the edit lease, and the
                    // patch then comes back 409. Silently dropping that would leave the toggle showing
                    // a state the server never accepted, so the rejection is surfaced.
                    patchStorefront({ id: storefront, patch }).unwrap().catch((e) => {
                        toast.error(storefrontSaveErrorMessage(e, 'Failed to save settings'));
                    });
                    originalSettingsRef.current = current;
                }
            }
        }, DEBOUNCE_MS);
    }, [patchStorefront, storefront]);

    if (storefrontQuery.isLoading || !localSettings) {
        return <div>Loading...</div>;
    }

    if (storefrontQuery.isError) {
        return <MessageBar messageBarType={MessageBarType.error}>Error loading storefront settings.</MessageBar>;
    }

    return (
        <div>
            <StorefrontSettingsTitleBar
                title="Authentication Settings"
                storefront={storefront}
                />
                <StorefrontReadOnlyRegion readOnly={readOnly}>
                <Pivot selectedKey={selectedTab} onLinkClick={(item) => setSelectedTab(item?.props.itemKey)}>
                    {/* Two tabs: this was one long list covering both halves of authentication, which
                        are configured at different times and answer different questions -- how a
                        shopper gets IN, versus who is allowed to ask in the first place. The sign-in
                        page signpost sits with Login because that is the page it opens, though the
                        editor behind it styles the sign-up page too. */}
                    <PivotItem headerText='Login' itemKey='login'>
                        <Stack tokens={{ childrenGap: '1em' }}>
                            <ToggleField
                                settingId='storefront.authentication.allowAnonymousAccess'
                                inline
                                label='Allow Anonymous Browsing'
                                toolTip='Allow guest users without known contact details to browse the storefront. These users will not be able to add to cart, view order, or place orders'
                                checked={localSettings.allowAnonymousAccess}
                                onChange={(e, checked) => {
                                    const updatedSettings = { ...localSettings, allowAnonymousAccess: checked || false };
                                    setLocalSettings(updatedSettings);
                                    doSave(updatedSettings);
                                }}
                            />
                            <ToggleField
                                settingId='storefront.authentication.anonymousShowPricing'
                                inline
                                label='Show Pricing For Anonymous Users'
                                toolTip='Show pricing information to users who are not signed in. This setting is only applicable if "Allow Anonymous Browsing" is enabled.'
                                checked={localSettings.anonymousShowPricing}
                                onChange={(e, checked) => {
                                    const updatedSettings = { ...localSettings, anonymousShowPricing: checked || false };
                                    setLocalSettings(updatedSettings);
                                    doSave(updatedSettings);
                                }}
                                disabled={!localSettings.allowAnonymousAccess}
                            />
                            {/* Depends on anonymous browsing for the same reason the pricing toggle
                                does: guest landing IS an anonymous session, and the storefront API
                                rejects one when the storefront doesn't allow it. Disabled rather than
                                hidden so the option is discoverable next to the toggle that enables it. */}
                            {/* Each dropdown and its caption are one group: the Stack's 1em gap then sits
                                BETWEEN settings rather than between a control and the text explaining it,
                                and the caption gets its own small offset clear of the control's border. */}
                            <div data-setting-id='storefront.authentication.loginMode'>
                                <Dropdown
                                    label='When a shopper opens the storefront'
                                    selectedKey={localSettings.loginMode || 'required'}
                                    disabled={!localSettings.allowAnonymousAccess}
                                    options={[
                                        { key: 'required', text: 'Show the sign-in page' },
                                        { key: 'guest', text: 'Take them straight to the home page as a guest' },
                                    ]}
                                    onChange={(e, option) => {
                                        const updatedSettings = { ...localSettings, loginMode: option?.key as string };
                                        setLocalSettings(updatedSettings);
                                        doSave(updatedSettings);
                                    }}
                                    styles={{ root: { maxWidth: 420 } }}
                                />
                                <div style={{ fontSize: 12, color: '#605e5c', marginTop: 6, maxWidth: 420 }}>
                                    Guests can still sign in from the account control in the header, and anyone
                                    following an emailed link signs in as themselves either way.
                                </div>
                            </div>
                            {/* Independent of anonymous browsing: this is how ANY shopper finishes a
                                sign-in, guest storefront or not. */}
                            <div data-setting-id='storefront.authentication.loginMethod'>
                                <Dropdown
                                    label='How shoppers finish signing in'
                                    selectedKey={localSettings.loginMethod || 'magicLink'}
                                    options={[
                                        { key: 'magicLink', text: 'Follow the link in the email' },
                                        { key: 'code', text: 'Type a code from the email, without leaving the page' },
                                        { key: 'poll', text: 'Open the link in a new tab, and sign in the page they were on' },
                                    ]}
                                    onChange={(e, option) => {
                                        const updatedSettings = { ...localSettings, loginMethod: option?.key as string };
                                        setLocalSettings(updatedSettings);
                                        doSave(updatedSettings);
                                    }}
                                    styles={{ root: { maxWidth: 420 } }}
                                />
                                <div style={{ fontSize: 12, color: '#605e5c', marginTop: 6, maxWidth: 420 }}>
                                    Codes are six digits, expire after 10 minutes, and are locked out after five
                                    wrong tries. Both in-page options still email a link as a fallback for
                                    shoppers who read their email on another device.
                                </div>
                            </div>
                            {/* Appearance lives in the Pages editor with every other page, where it is
                                edited against a live preview. This is the signpost from the settings
                                that decide what these pages DO to the ones that decide how they look —
                                one editing surface, not two that can disagree. */}
                            <div>
                                <DefaultButton
                                    text='Customize the sign-in pages…'
                                    iconProps={{ iconName: 'Color' }}
                                    onClick={() => props.history.push(`/settings/storefront/storefronts/${storefront}/pages?tab=login`)}
                                />
                                <div style={{ fontSize: 12, color: '#605e5c', marginTop: 6, maxWidth: 420 }}>
                                    Opens the Sign In page editor: background, logo, wording and styling for
                                    the sign-in and sign-up pages, and the pages an emailed link lands on.
                                </div>
                            </div>
                        </Stack>
                    </PivotItem>
                    <PivotItem headerText='Signup' itemKey='signup'>
                        <Stack tokens={{ childrenGap: '1em' }}>
                            <ToggleField
                                settingId='storefront.authentication.allowSignup'
                                inline
                                label='Allow Signup'
                                toolTip='Allow users to create an account on the storefront. If you have a customer lookup extension configured, we will attempt to match the user to the contact in your system based on the information they provide during signup.'
                                checked={localSettings.allowSignup}
                                onChange={(e, checked) => {
                                    const updatedSettings = { ...localSettings, allowSignup: checked || false };
                                    setLocalSettings(updatedSettings);
                                    doSave(updatedSettings);
                                }}
                            />
                            {/* The signup gate. Only meaningful when signup is on, so it sits with it
                                rather than with the anonymous-browsing toggles above. */}
                            <div data-setting-id='storefront.authentication.unresolvedSignupPolicy'>
                                <Dropdown
                                    label='When someone signs up and we cannot match them to a contact'
                                    selectedKey={localSettings.unresolvedSignupPolicy || 'allow'}
                                    disabled={!localSettings.allowSignup}
                                    options={[
                                        { key: 'allow', text: 'Let them in straight away' },
                                        { key: 'requireApproval', text: 'Hold them for approval' },
                                    ]}
                                    onChange={(e, option) => {
                                        const updatedSettings = { ...localSettings, unresolvedSignupPolicy: option?.key as string };
                                        setLocalSettings(updatedSettings);
                                        doSave(updatedSettings);
                                    }}
                                    styles={{ root: { maxWidth: 420 } }}
                                />
                                <div style={{ fontSize: 12, color: '#605e5c', marginTop: 6, maxWidth: 420 }}>
                                    Anyone your customer lookup extension recognises is linked and let in either
                                    way. With no lookup extension configured nobody is recognised, so
                                    &quot;hold for approval&quot; will queue every signup.
                                </div>
                            </div>
                            <ConditionalSetting
                                when={localSettings.unresolvedSignupPolicy === 'requireApproval'}
                                id='storefront.authentication.autoApproveDomains'
                            >
                                <div data-setting-id='storefront.authentication.autoApproveDomains'>
                                    <TextField
                                        label='Approve these domains automatically'
                                        placeholder='acme.com, acme.co.uk'
                                        value={localSettings.autoApproveDomains ?? ''}
                                        disabled={!localSettings.allowSignup}
                                        onChange={(e, v) => setLocalSettings({ ...localSettings, autoApproveDomains: v ?? '' })}
                                        onBlur={() => doSave(localSettings)}
                                        styles={{ root: { maxWidth: 420 } }}
                                    />
                                    <div style={{ fontSize: 12, color: '#605e5c', marginTop: 6, maxWidth: 420 }}>
                                        Comma separated. Matches the whole domain, so acme.com admits
                                        ada@acme.com but not ada@mail.acme.com. Everyone else waits on the
                                        Users screen.
                                    </div>
                                </div>
                            </ConditionalSetting>
                            <RichTextField
                                settingId='storefront.authentication.signupTermsAndConditions'
                                label='Signup Terms and Conditions'
                                mode='markdown'
                                value={localSettings.signupTermsAndConditions}
                                toolTip='Markdown allowed'
                                onChange={(value) => {
                                    const updatedSettings = { ...localSettings, signupTermsAndConditions: value };
                                    setLocalSettings(updatedSettings);
                                    doSave(updatedSettings);
                                }}
                                disabled={!localSettings.allowSignup}
                            />
                            {/* Signup notifications. Sits with the signup settings rather than on the
                                Ordering page's email list because the approval variant only means
                                anything next to the gate that produces it. Blank address = both
                                emails off; the webhooks are configured separately and fire either way. */}
                            <div data-setting-id='storefront.authentication.signupNotificationEmail'>
                                <TextField
                                    label='Notify these addresses of signups'
                                    placeholder='ops@acme.com, sales@acme.com'
                                    value={localSettings.signupNotificationEmail ?? ''}
                                    disabled={!localSettings.allowSignup}
                                    onChange={(e, v) => setLocalSettings({ ...localSettings, signupNotificationEmail: v ?? '' })}
                                    onBlur={() => doSave(localSettings)}
                                    styles={{ root: { maxWidth: 420 } }}
                                />
                                <div style={{ fontSize: 12, color: '#605e5c', marginTop: 6, maxWidth: 420 }}>
                                    Comma separated, and your staff rather than the shopper. Leave empty to
                                    send nothing. Held signups are emailed here too, which is the only thing
                                    that announces someone is waiting on the Users screen.
                                </div>
                            </div>
                            <Stack tokens={{ childrenGap: '0.5em' }}>
                                <ConditionalSetting
                                    when={!!localSettings.signupNotificationEmail}
                                    ids={['storefront.authentication.signupEmailSubject', 'storefront.authentication.signupEmailBody']}
                                >
                                    <div data-setting-id='storefront.authentication.signupEmailSubject'>
                                        <TextField
                                            label='Signup subject'
                                            placeholder='Leave empty for the default'
                                            value={localSettings.signupEmailSubject ?? ''}
                                            disabled={!localSettings.allowSignup}
                                            onChange={(e, v) => setLocalSettings({ ...localSettings, signupEmailSubject: v ?? '' })}
                                            onBlur={() => doSave(localSettings)}
                                            styles={{ root: { maxWidth: 420 } }}
                                        />
                                    </div>
                                    <RichTextField
                                        settingId='storefront.authentication.signupEmailBody'
                                        label='Signup body'
                                        value={localSettings.signupEmailBody}
                                        toolTip='Leave empty for the default. Tokens: {{{storefront}}}, {{{customer_first_name}}}, {{{customer_last_name}}}, {{{customer_email}}}, {{{customer_company}}}, {{{storefront_link}}}, {{{storefront_users_link}}}'
                                        onChange={(value) => {
                                            const updatedSettings = { ...localSettings, signupEmailBody: value };
                                            setLocalSettings(updatedSettings);
                                            doSave(updatedSettings);
                                        }}
                                        disabled={!localSettings.allowSignup}
                                    />
                                </ConditionalSetting>
                                <ConditionalSetting
                                    when={!!localSettings.signupNotificationEmail && localSettings.unresolvedSignupPolicy === 'requireApproval'}
                                    ids={['storefront.authentication.signupApprovalEmailSubject', 'storefront.authentication.signupApprovalEmailBody']}
                                >
                                    <div data-setting-id='storefront.authentication.signupApprovalEmailSubject'>
                                        <TextField
                                            label='Awaiting approval subject'
                                            placeholder='Leave empty for the default'
                                            value={localSettings.signupApprovalEmailSubject ?? ''}
                                            disabled={!localSettings.allowSignup}
                                            onChange={(e, v) => setLocalSettings({ ...localSettings, signupApprovalEmailSubject: v ?? '' })}
                                            onBlur={() => doSave(localSettings)}
                                            styles={{ root: { maxWidth: 420 } }}
                                        />
                                    </div>
                                    <RichTextField
                                        settingId='storefront.authentication.signupApprovalEmailBody'
                                        label='Awaiting approval body'
                                        value={localSettings.signupApprovalEmailBody}
                                        toolTip='Leave empty for the default. Same tokens as the signup body.'
                                        onChange={(value) => {
                                            const updatedSettings = { ...localSettings, signupApprovalEmailBody: value };
                                            setLocalSettings(updatedSettings);
                                            doSave(updatedSettings);
                                        }}
                                        disabled={!localSettings.allowSignup}
                                    />
                                </ConditionalSetting>
                            </Stack>
                            {/* The one signup email a CUSTOMER reads. Kept outside the staff block
                                above and shown whenever the gate is on, because it is sent by the
                                Approve button rather than by a signup, and it goes out regardless of
                                whether staff notifications are configured. */}
                            <ConditionalSetting
                                when={localSettings.unresolvedSignupPolicy === 'requireApproval'}
                                ids={['storefront.authentication.signupApprovedEmailSubject', 'storefront.authentication.signupApprovedEmailBody']}
                            >
                                <Stack tokens={{ childrenGap: '0.5em' }}>
                                    <div data-setting-id='storefront.authentication.signupApprovedEmailSubject'>
                                        <TextField
                                            label='Approved email subject'
                                            placeholder='Leave empty for the default'
                                            value={localSettings.signupApprovedEmailSubject ?? ''}
                                            disabled={!localSettings.allowSignup}
                                            onChange={(e, v) => setLocalSettings({ ...localSettings, signupApprovedEmailSubject: v ?? '' })}
                                            onBlur={() => doSave(localSettings)}
                                            styles={{ root: { maxWidth: 420 } }}
                                        />
                                    </div>
                                    <RichTextField
                                        settingId='storefront.authentication.signupApprovedEmailBody'
                                        label='Approved email body'
                                        // Source-only, like every other storefront email template.
                                        // The body is HTML with {{{token}}} literals in it, which the
                                        // WYSIWYG view renders as ordinary text with no sign they are
                                        // special -- and it is the only view the typeahead works in.
                                        rawOnly
                                        mode='html'
                                        // Falls back to the API's own default so the editor opens on
                                        // real content rather than a blank box. Editing it saves your
                                        // version; leaving it alone saves nothing, so the field stays
                                        // blank and the API keeps sending the default.
                                        value={localSettings.signupApprovedEmailBody || approvedDefault.data?.body}
                                        // Lives in the editor overlay rather than beside the label:
                                        // the overlay masks the page, so a button out here is dead
                                        // exactly when the tokens are being typed, and having it in
                                        // both places just means one of them never works.
                                        editorActions={approvedTokenReference}
                                        tokenSuggestions={approvedTokens.suggestions}
                                        toolTip='Sent to the shopper when you approve them. Clear it to go back to the default.'
                                        onChange={(value) => {
                                            const updatedSettings = { ...localSettings, signupApprovedEmailBody: value };
                                            setLocalSettings(updatedSettings);
                                            doSave(updatedSettings);
                                        }}
                                        disabled={!localSettings.allowSignup}
                                    />
                                    <div style={{ fontSize: 12, color: '#605e5c', maxWidth: 420 }}>
                                        Keep {'{{{storefront_login_link}}}'} so they have a way back in. It
                                        points at the sign-in page rather than signing them in directly.
                                    </div>
                                </Stack>
                            </ConditionalSetting>
                        </Stack>
                    </PivotItem>
                </Pivot>
                </StorefrontReadOnlyRegion>
        </div>
    );
}

export default StorefrontAuthenticationSettingsPage;